import dayjs from 'dayjs';
import 'dayjs/locale/uk';
import 'dayjs/locale/en';
import { primitiveColors, textColor } from '../theming';

export const MAX_RANGE_DAYS = 90;

const PROCESSING_STATES = ['running', 'paused', 'polling_paused', 'disabled_by_env'];

export const RANGE_PRESETS = ['24h', '7d', '30d'];

const PRESET_SPANS = { '24h': [24, 'hour'], '7d': [7, 'day'], '30d': [30, 'day'] };

// the @nestjs/schedule CronExpression values the API reports in `schedule`
const CADENCES = { '0 0-23/1 * * *': 'everyHour', '0 */30 * * * *': 'every30Minutes' };

const isBool = value => typeof value === 'boolean';
const isNum = value => typeof value === 'number' && Number.isFinite(value);
const isObj = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const isStrOrNull = value => value === null || typeof value === 'string';
const hasMinMax = value => isObj(value) && isNum(value.min) && isNum(value.max);

const validBounds = bounds =>
    isObj(bounds) && hasMinMax(bounds.confidence) && isNum(bounds.confidence.step) && hasMinMax(bounds.batchCarLimit);

const validSchedule = schedule =>
    isObj(schedule) &&
    typeof schedule.trigger === 'string' &&
    typeof schedule.polling === 'string' &&
    isStrOrNull(schedule.nextTriggerAt);

/**
 * The GET /admin/vin-extraction/settings body (also returned by every write) if it has the fields the strip
 * and the edit form render, else null — so an unexpected shape is reported, never half-rendered.
 */
export const parseSettings = data => {
    if (!isObj(data)) return null;
    const flagsOk = isBool(data.enabledByEnv) && isBool(data.batchTriggerPaused) && isBool(data.modelAllowed);
    const valuesOk = isNum(data.confidenceThreshold) && Number.isInteger(data.batchCarLimit) && isStrOrNull(data.model);
    const listOk = Array.isArray(data.allowedModels) && data.allowedModels.every(model => typeof model === 'string');
    const shapeOk = validBounds(data.bounds) && validSchedule(data.schedule) && Number.isInteger(data.version);

    return flagsOk && valuesOk && listOk && shapeOk && PROCESSING_STATES.includes(data.state) ? data : null;
};

/** The Processing switch: on while the batch job may run (polling paused still counts as on). */
export const isProcessingOn = settings => settings.enabledByEnv === true && settings.batchTriggerPaused === false;

const EDITABLE_FIELDS = ['confidenceThreshold', 'batchCarLimit', 'model', 'thinkingLevel'];

/** The thinking levels a model accepts; [] for a model that takes none, or a body from before the field. */
export const thinkingLevelsFor = (settings, model) => {
    const levels = settings.thinkingLevelsByModel?.[model];
    return Array.isArray(levels) ? levels : [];
};

/** The PATCH body: the version the admin started from plus only the changed fields; null when nothing changed. */
export const changedSettingsFields = (base, values) => {
    const changed = EDITABLE_FIELDS.filter(
        field => values[field] !== null && values[field] !== undefined && values[field] !== base[field]
    );

    if (changed.length === 0) return null;

    return changed.reduce((body, field) => ({ ...body, [field]: values[field] }), { version: base.version });
};

/**
 * The API's error `code`. The global ValidationPipe answers a bad body or query with a 400 that has no `code`
 * (`{statusCode, message: string[], error}`), so a 400 without one reads as VALIDATION_FAILED.
 */
export const apiErrorCode = error => {
    const code = error?.response?.data?.code;
    if (typeof code === 'string') return code;

    return error?.response?.status === 400 ? 'VALIDATION_FAILED' : null;
};

export const isVersionConflict = error => error?.response?.status === 409 && apiErrorCode(error) === 'VERSION_CONFLICT';

/** A rolling preset window ending at `now`. */
export const presetRange = (preset, now = dayjs()) => {
    const [amount, unit] = PRESET_SPANS[preset];

    return { from: now.subtract(amount, unit), to: now };
};

export const isRangeTooLong = (from, to) => to.diff(from, 'minute') > MAX_RANGE_DAYS * 24 * 60;

/** The i18n key for a known cron cadence, or null so the raw expression is shown instead of a wrong label. */
export const cadenceKey = cron => CADENCES[cron] ?? null;

// ---- GET /admin/vin-extraction/stats ----

const STATS_BUCKETS = ['hour', 'day'];

const FAILURE_REASONS = [
    'length_mismatch',
    'wmi_mismatch',
    'checksum_failed',
    'low_confidence',
    'job_failed',
    'unknown',
];

const isStr = value => typeof value === 'string';
const isNumOrNull = value => value === null || isNum(value);

const validStatsRange = range =>
    isObj(range) && isStr(range.from) && isStr(range.to) && isStr(range.tz) && STATS_BUCKETS.includes(range.bucket);

const validCurrent = current =>
    isObj(current) &&
    isNumOrNull(current.pending) &&
    isNumOrNull(current.inProgress) &&
    // the all-time totals (SC-QA-32); absent from an API without them
    (current.processed === undefined || isNumOrNull(current.processed)) &&
    (current.withVin === undefined || isNumOrNull(current.withVin)) &&
    isStrOrNull(current.cachedAt) &&
    isBool(current.unavailable);

const validTotals = totals =>
    isObj(totals) &&
    isNum(totals.success) &&
    isNum(totals.failed) &&
    isNum(totals.notFound) &&
    isNumOrNull(totals.successRate) &&
    (totals.notFoundRate === undefined || isNumOrNull(totals.notFoundRate)) &&
    isNum(totals.recoveredByRetry);

const validBucket = item =>
    isObj(item) && isStr(item.bucketStart) && isNum(item.success) && isNum(item.failed) && isNum(item.notFound);

const validReason = item => isObj(item) && isStr(item.reason) && isNum(item.count) && isNum(item.share);

/**
 * The GET /admin/vin-extraction/stats body if it has every field the cards, the chart and the failure reasons
 * render, else null — so an unexpected shape is reported, never half-rendered.
 */
export const parseStats = data => {
    if (!isObj(data)) return null;
    const headOk = validStatsRange(data.range) && isStrOrNull(data.recordedSince) && validCurrent(data.current);
    const totalsOk = validTotals(data.totals) && validTotals(data.previous);
    const listsOk =
        Array.isArray(data.series) &&
        data.series.every(validBucket) &&
        Array.isArray(data.failureReasons) &&
        data.failureReasons.every(validReason);

    return headOk && totalsOk && listsOk ? data : null;
};

const direction = value => {
    if (value > 0) return 'up';
    if (value < 0) return 'down';
    return 'flat';
};

/**
 * Change vs the previous window in % of the previous value: `{direction, value}`. A previous 0 gives no
 * percentage (`value: null`, read as "new") when something appeared, and null when both are 0; null when
 * either side is missing.
 */
export const percentDelta = (current, previous) => {
    if (!isNum(current) || !isNum(previous)) return null;
    if (previous === 0) return current === 0 ? null : { direction: direction(current), value: null };
    const value = ((current - previous) / previous) * 100;

    return { direction: direction(value), value };
};

/** Change of a rate (a 0–1 fraction) in percentage points: `{direction, value}`, or null when either is null. */
export const ppDelta = (currentRate, previousRate) => {
    if (!isNum(currentRate) || !isNum(previousRate)) return null;
    const value = (currentRate - previousRate) * 100;

    return { direction: direction(value), value };
};

/** 'good' | 'bad' | 'neutral' for a delta; `downIsGood` for Failed and Not found. */
export const deltaTone = (delta, { downIsGood = false } = {}) => {
    if (!delta || delta.direction === 'flat') return 'neutral';

    return (delta.direction === 'up') !== downIsGood ? 'good' : 'bad';
};

// preset window lengths in hours; a custom range of whole local days can be an hour off across a DST change
const PERIOD_HOURS = [
    ['24h', 24],
    ['7d', 7 * 24],
    ['30d', 30 * 24],
];

/** The "vs prev. <label>" key for a range of ISO strings: '24h' | '7d' | '30d', else 'period'. */
export const comparisonPeriod = (from, to) => {
    const hours = dayjs(to).diff(dayjs(from), 'minute') / 60;
    const match = PERIOD_HOURS.find(([, length]) => Math.abs(hours - length) <= 1);

    return match ? match[0] : 'period';
};

/**
 * A chart label in the browser's local tz: 'HH:mm' of the local start for an hourly bucket (buckets are UTC hours,
 * so a half-hour zone reads '15:30', a whole-hour zone '15:00'), 'DD-MM-YYYY' for a daily one.
 */
export const bucketLabel = (bucketStart, bucket) =>
    dayjs(bucketStart).format(bucket === 'day' ? 'DD-MM-YYYY' : 'HH:mm');

const CHART_SERIES = [
    { key: 'success', color: textColor.success },
    { key: 'failed', color: textColor.error, dashed: true },
    { key: 'notFound', color: primitiveColors.gray400, dashed: true },
];

/**
 * `{labels, datasets}` for `LineChart`: one label per bucket, by position (a DST fall-back hour repeats a label
 * and stays its own point), and Success solid / Failed and Not found dashed. `names` = {success, failed, notFound}.
 */
export const buildChartData = (series, bucket, names) => ({
    labels: series.map(item => bucketLabel(item.bucketStart, bucket)),
    datasets: CHART_SERIES.map(({ key, color, dashed }) => ({
        label: names[key],
        data: series.map(item => item[key]),
        borderColor: color,
        backgroundColor: color,
        borderWidth: 2,
        ...(dashed ? { borderDash: [5, 3] } : {}),
        fill: false,
        cubicInterpolationMode: 'monotone',
        tension: 0.3,
        pointBackgroundColor: primitiveColors.gray0,
    })),
});

/** True when no bucket holds an outcome (an empty range still renders its dense zero buckets). */
export const isSeriesEmpty = series => series.every(item => !item.success && !item.failed && !item.notFound);

/** The "Stats are recorded since" notice: only when recording started after the range's start. */
export const showsRecordedSince = (recordedSince, from) =>
    recordedSince !== null && recordedSince !== undefined && dayjs(recordedSince).isAfter(dayjs(from));

/**
 * The vehicle database card (SC-QA-102): every car is processed (with or without a VIN), in progress or pending.
 * Null while the count is unavailable, or from an API without the processed totals.
 */
export const databaseProgress = current => {
    if (!current || current.unavailable || !isNum(current.processed) || !isNum(current.pending)) return null;
    const withVin = isNum(current.withVin) ? current.withVin : 0;
    const inProgress = isNum(current.inProgress) ? current.inProgress : 0;
    const total = current.processed + inProgress + current.pending;

    return {
        total,
        processed: current.processed,
        withVin,
        noVin: Math.max(0, current.processed - withVin),
        inProgress,
        pending: current.pending,
        // SC-QA-108: pending cars with no registration or COC image, never processed; null when not counted
        noDocuments: isNum(current.noDocuments) ? Math.min(current.noDocuments, current.pending) : null,
        share: total === 0 ? null : current.processed / total,
    };
};

/**
 * SC-QA-113: the last wave's empty check, for the vehicle database card's warning, or null. Shown only while processing
 * runs and only for a well-formed check whose sampled cars had no document; an API without the field reads as none.
 */
export const emptyWaveWarning = settings => {
    const check = settings?.emptyWave;
    if (settings?.state !== 'running' || !isObj(check)) return null;
    const valid =
        Number.isInteger(check.sampled) &&
        check.sampled > 0 &&
        check.withDocuments === 0 &&
        typeof check.checkedAt === 'string' &&
        !Number.isNaN(Date.parse(check.checkedAt));
    return valid ? check : null;
};

/**
 * SC-QA-126: the note under the vehicle database card, only while processing runs and once the cars without documents
 * are counted. Cars left to reserve or in progress: none (the warning used to say nothing could be reserved while
 * Pending showed cars). Nothing left: `scraper` when the last wave's check found no document on the newest pending
 * cars (new cars arrive without them), else `allDone`.
 */
export const databaseNote = (progress, settings) => {
    if (!progress || progress.noDocuments === null || settings?.state !== 'running') return null;
    if (progress.pending - progress.noDocuments > 0 || progress.inProgress > 0) return null;
    const emptyWave = emptyWaveWarning(settings);
    return emptyWave ? { kind: 'scraper', emptyWave } : { kind: 'allDone' };
};

/**
 * SC-QA-108: hours until the processor has taken every car it can, at the schedule's full speed: each hourly run
 * starts `concurrentBatches` batches of `batchCarLimit` cars. Cars without documents are left out (they are never
 * taken). Only while processing is running; null otherwise, or when there is no pace to use; 0 when nothing is left. A run
 * waits for the last one to finish, so this is the fastest it can go, not a promise.
 * SC-QA-123: null until the cars without documents are counted. Counting them as cars still to process doubled the
 * time on production (564,878 pending, about half without documents) until that count came in, so it jumped.
 */
export const hoursToFinish = (progress, settings) => {
    if (!progress || progress.noDocuments === null || settings?.state !== 'running') return null;
    const perHour = (settings.concurrentBatches ?? 0) * (settings.batchCarLimit ?? 0);
    if (perHour <= 0) return null;
    const left = progress.pending - progress.noDocuments + progress.inProgress;
    // SC-QA-124: nothing to reserve and nothing in progress is 0, not "no estimate"; cars in progress still count
    return Math.max(left, 0) / perHour;
};

/** The i18n key for a failure reason code; an unrecognised code reads as 'unknown' (its raw code is still shown). */
export const failureReasonKey = reason => (FAILURE_REASONS.includes(reason) ? reason : 'unknown');

// ---- GET /admin/vin-extraction/batches, /batches/:id/cars, /cars/:carId/documents ----

export const BATCH_RESULTS = ['processing', 'success', 'failed'];

const FAILURE_KINDS = ['gemini_job', 'submission'];

// Why a batch failed (SC-QA-07); the API stores it as free text, so an unknown code reads as 'unknown'.
const BATCH_FAILURE_CODES = [
    'credits_depleted',
    'rate_limited',
    'auth',
    'model_unavailable',
    'invalid_request',
    'service_unavailable',
    'no_documents',
    'job_failed',
    'job_cancelled',
    'job_expired',
    'job_lost',
    'unknown',
];

/** The i18n key for a batch failure code; an unrecognised code reads as 'unknown'. */
export const batchFailureCodeKey = code => (BATCH_FAILURE_CODES.includes(code) ? code : 'unknown');

const CAR_STATUSES = ['pending', 'in_progress', 'success', 'failed', 'not_found'];

/** The cars panel's tabs → the API's `status` param (All sends none) and the `counts` field each tab shows. */
export const CAR_TABS = [
    { key: 'all', status: undefined, count: 'all' },
    { key: 'success', status: 'success', count: 'success' },
    { key: 'failed', status: 'failed', count: 'failed' },
    { key: 'not_found', status: 'not_found', count: 'notFound' },
];

export const BATCHES_PAGE_SIZES = [8, 20];
export const CARS_PAGE_SIZE = 6;
export const CARS_PAGE_SIZE_ALL = 100;
const SEARCH_MIN_LENGTH = 2;
export const SEARCH_MAX_LENGTH = 32;
export const SEARCH_DEBOUNCE_MS = 300;
const DOCUMENT_PARTS = ['registration', 'coc'];

const JOB_NAME_MAX = 16;

const isId = value => isStr(value) && value.length > 0;
const isNullOr = (value, check) => value === null || check(value);
const isOneOf = list => value => list.includes(value);
const isCount = value => Number.isInteger(value) && value >= 0;

const validCurrentCounts = counts =>
    isObj(counts) && isCount(counts.success) && isCount(counts.failed) && isCount(counts.notFound);

const validBatchItem = item =>
    isObj(item) &&
    isId(item.id) &&
    isStrOrNull(item.jobName) &&
    isStr(item.createdAt) &&
    isNullOr(item.result, isOneOf(BATCH_RESULTS)) &&
    isCount(item.carCount) &&
    isStrOrNull(item.model) &&
    validCurrentCounts(item.current) &&
    isNullOr(item.failureKind, isOneOf(FAILURE_KINDS)) &&
    // absent from an API older than the field: read as null
    isStrOrNull(item.failureCode ?? null) &&
    isStrOrNull(item.failureMessage ?? null) &&
    isBool(item.stuck) &&
    isNumOrNull(item.processingFor);

const isPage = data => isObj(data) && isCount(data.total) && Array.isArray(data.items);

/** The GET /batches body ({total, items}) if every row has what the table renders, else null. */
export const parseBatches = data => (isPage(data) && data.items.every(validBatchItem) ? data : null);

const validError = error => isObj(error) && isStr(error.code) && isStrOrNull(error.message);

const validAttempt = attempt =>
    isObj(attempt) &&
    Number.isInteger(attempt.attemptNumber) &&
    isOneOf(['batch', 'live_retry'])(attempt.kind) &&
    isStrOrNull(attempt.candidateVin) &&
    isNumOrNull(attempt.confidence) &&
    isBool(attempt.lowConfidence) &&
    Array.isArray(attempt.validationErrors) &&
    attempt.validationErrors.every(validError) &&
    isStrOrNull(attempt.model) &&
    isStr(attempt.createdAt);

const validCar = car =>
    isObj(car) &&
    isId(car.carId) &&
    isStrOrNull(car.stockNumber) &&
    isStrOrNull(car.manufacturer) &&
    isStrOrNull(car.model) &&
    isStrOrNull(car.vin) &&
    CAR_STATUSES.includes(car.status) &&
    isBool(car.viaLiveRetry) &&
    // optional while an older API (without the previous-VIN fields) is still rolling out
    (car.previousVin === undefined || isStrOrNull(car.previousVin)) &&
    (car.previousVinMatch === undefined || isNullOr(car.previousVinMatch, isBool)) &&
    Array.isArray(car.attempts) &&
    car.attempts.every(validAttempt);

const validSnapshot = snapshot => isObj(snapshot) && isStr(snapshot.model) && isNum(snapshot.confidenceThreshold);

const validCarsBatch = batch =>
    isObj(batch) &&
    isId(batch.id) &&
    isStrOrNull(batch.jobName) &&
    isStr(batch.createdAt) &&
    isNullOr(batch.result, isOneOf(BATCH_RESULTS)) &&
    isNullOr(batch.snapshot, validSnapshot);

/** The GET /batches/:id/cars body if the batch, the tab counts and every car have what the panel and drawer render. */
export const parseBatchCars = data =>
    isPage(data) &&
    validCarsBatch(data.batch) &&
    validCurrentCounts(data.counts) &&
    isCount(data.counts.all) &&
    data.items.every(validCar)
        ? data
        : null;

/**
 * The URL itself when it is https:, else null — the client-side second guard behind the API's own filter, so a
 * `javascript:`, `data:` or plain-http link from the external DB never reaches an href (E30).
 */
export const safeDocumentUrl = url => {
    if (!isStr(url)) return null;
    try {
        return new URL(url).protocol === 'https:' ? url : null;
    } catch (error) {
        return null;
    }
};

/** The GET /cars/:carId/documents body with only its https: links kept; null when the shape is wrong. */
export const parseCarDocuments = data => {
    if (!isObj(data) || !Array.isArray(data.items)) return null;
    if (!data.items.every(item => isObj(item) && isStr(item.part) && isStr(item.url))) return null;

    return { items: data.items.filter(item => safeDocumentUrl(item.url) !== null) };
};

/** A document link's label: the i18n key of its part (an unknown part → 'other') and its 1-based number. */
export const documentLabel = (part, index) => ({
    key: `drawer.documents.part.${DOCUMENT_PARTS.includes(part) ? part : 'other'}`,
    number: index + 1,
});

/** The documents with a per-part index (Registration 1, CoC 1, Registration 2) and a stable React key. */
export const documentLinks = items => {
    const seen = {};

    return items.map((item, position) => {
        const index = seen[item.part] ?? 0;
        seen[item.part] = index + 1;

        return { ...item, index, key: `${position}-${item.part}` };
    });
};

/** The attempt that decided the car: the highest attempt number; null when there is none (E15). */
export const finalAttempt = attempts =>
    Array.isArray(attempts) && attempts.length
        ? attempts.reduce((best, attempt) => (attempt.attemptNumber > best.attemptNumber ? attempt : best))
        : null;

/** The tag for a `checksum_failed` where the check digit is optional (outside North America and China): not an error. */
export const NO_CHECK_DIGIT_TAG = 'no_check_digit';

const tagCode = (attempt, code) =>
    attempt.checkDigitOptional === true && code === 'checksum_failed' ? NO_CHECK_DIGIT_TAG : code;

/**
 * An attempt's error tags: its distinct stored codes, plus `low_confidence` when it was under the threshold. A
 * checksum failure on a check-digit-optional VIN shows as `no_check_digit`.
 */
export const errorTags = attempt => {
    if (!attempt) return [];
    const codes = attempt.validationErrors.map(error => tagCode(attempt, error.code));
    if (attempt.lowConfidence) codes.push('low_confidence');

    return [...new Set(codes)];
};

/** How long a batch has been processing, as "Xh Ym" ("Ym" under an hour); null without a valid duration. */
export const formatProcessingFor = (ms, units) => {
    if (!isNum(ms) || ms < 0) return null;
    const totalMinutes = Math.floor(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = `${totalMinutes % 60}${units.minute}`;

    return hours ? `${hours}${units.hour} ${minutes}` : minutes;
};

/** A Gemini job name cut to 16 characters plus "…" for the table; the full name stays in the copy button. */
export const truncateJobName = name =>
    isStr(name) && name.length > JOB_NAME_MAX ? `${name.slice(0, JOB_NAME_MAX)}…` : name;

/** The cars `search` param: the trimmed text when it is 2–32 characters, else undefined (no search). */
export const searchParam = text => {
    const trimmed = isStr(text) ? text.trim() : '';

    return trimmed.length >= SEARCH_MIN_LENGTH && trimmed.length <= SEARCH_MAX_LENGTH ? trimmed : undefined;
};

/** The 1-based position of row `index` of `page` among all the failed cars, for "k of N failed". */
export const failedPosition = ({ page, pageSize, index }) => (page - 1) * pageSize + index + 1;

/**
 * The previous (`step` -1) or next (+1) failed car from row `index` of `page`: `{page, index, fetch}`, where `fetch`
 * means the neighbour is on another page that has to be loaded first; null before the first or after the last (E22).
 */
export const failedNeighbour = ({ page, pageSize, itemsLength, total, index }, step) => {
    const target = (page - 1) * pageSize + index + step;
    if (target < 0 || target >= total) return null;
    const targetPage = Math.floor(target / pageSize) + 1;
    const targetIndex = target % pageSize;
    if (targetPage === page && targetIndex >= itemsLength) return null;

    return { page: targetPage, index: targetIndex, fetch: targetPage !== page };
};

/** A date in the browser's tz with the UI language's month names. */
export const formatLocal = (value, pattern, lng) =>
    dayjs(value)
        .locale(lng === 'uk' ? 'uk' : 'en')
        .format(pattern);
